import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { WorkflowEdit } from "../../../../api/content/versionDecoders";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { THREAD_STEP_PARAMETER } from "../../../../shared/routing/appPaths";
import { usePaneQuery } from "../../../../shell/workspace";
import AppButton from "../../../../ui/buttons/AppButton";
import Chip from "../../../../ui/feedback/Chip";
import { refusalText } from "../../../../ui/feedback/refusalWords";
import { classNames } from "../../../../ui/styles/classNames";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import WorkflowEditButtons from "../../../versions/workflowEdit/WorkflowEditButtons";
import { OptionalChip } from "../../../workflows/graph/NodeControls";
import StepKindMark from "../../../workflows/graph/StepKindMark";
import { stepKindOfDefinition } from "../../../workflows/graph/stepKind";
import { editIntent, useHerePath, useThreadPreparation, type ItemAtVersion } from "../../prepare";
import PlannedInputsList from "../../stepPlans/PlannedInputsList";
import { namesText, reasonText } from "../../stepPlans/planText";
import { usePromptParts } from "../../stepPlans/usePromptParts";
import { useThreadContext } from "../threadPageContext";
import type { StepRunner } from "../useStepRunner";
import ChangeStepMenu from "./ChangeStepMenu";
import RunsWithCard from "../../llmChoice/RunsWithCard";
import MarkdownRenderer from "../../../../ui/content/MarkdownRenderer";
import { requestTranslation } from "../../entries/humanStepResponse";
import EntryQuestion from "./EntryQuestion";
import { stepInputs } from "./stepInputs";
import type { RunDraft } from "./useRunDraft";
import type { StepFocusView } from "./useStepFocus";
import WaitingStepView from "./WaitingStepView";
import { actionOffer } from "../viewerActions";
import StepReads from "../steps/StepReads";
import { STEP_STATE_TONE } from "../../status/stepStateTone";
import { stepStateText } from "../../status/stepStateWords";

interface CurrentStepProps {
  focus: StepFocusView;
  runner: StepRunner;
  /** The values typed into this step, held by the step list (PC-3). */
  draft: RunDraft;
  /** The earlier steps that ran (or a run skipped, PC-4) and this viewer may run again, each by its own decision: the step list's. */
  earlier: readonly string[];
}

/**
 * The step the Thread is at, the one open step of the Workflow's steps (F33, R-11, F100), as
 * slim as its use (F56): the step, what it reads from the project (F103), one question where
 * an input is missing, Run, and "Change step".
 * Module B's plan decides what is asked; what it fills folds under "Filled automatically".
 * Waiting for you holds the person step's answer; Running shows its chip, and Cancel is by
 * the message box; Waiting for approval says what the host's PC must approve, and the step
 * runs once it is approved (TR-B1-01); Failed offers Try again. Nothing failed is retried on
 * its own: a call that may have reached the provider is never repeated automatically (T-11).
 */
export default function CurrentStep({ focus, runner, draft, earlier }: CurrentStepProps) {
  const { t, i18n } = useTranslation();
  const { threadId, overview, viewerIsHost, openTurn, stepName, currentStep: section, highlightedEntry } = useThreadContext();
  const preparation = useThreadPreparation();
  const here = useHerePath();
  const [editing, setEditing] = useState(false);
  const { current: focused } = focus;
  const [query] = usePaneQuery();
  const addressedStep = query.get(THREAD_STEP_PARAMETER);
  // An address naming this step (a review's owning step, ADR-0031 D4) brings it into view once.
  const addressedHere = addressedStep !== null && addressedStep === focused?.stepKey;
  useEffect(() => {
    if (addressedHere) section.current?.scrollIntoView({ block: "start" });
  }, [addressedHere, section]);
  // A step that waits for you opens at its top, once for each request, not at the log's end above it (usability round
  // 1, 4 Nov: the first screen showed the log's tail and one line of what the step asks to check). An address that names
  // an entry or a step leads instead (Sol review 2, R4).
  const addressed = highlightedEntry !== null || addressedStep !== null;
  const waitingRequest = focused?.state === "waitingForYou" && !addressed ? (focus.waitingRequest?.number ?? null) : null;
  const shownRequest = useRef<number | null>(null);
  useEffect(() => {
    if (waitingRequest === null || shownRequest.current === waitingRequest) return;
    shownRequest.current = waitingRequest;
    // After the log has followed its end on this render, so the step wins.
    requestAnimationFrame(() => { section.current?.scrollIntoView({ block: "start" }); });
  }, [waitingRequest, section]);
  const { current, values: runValues, plan: draftPlan, pending: draftPending } = draft;
  // What an agent step's prompt fills itself, before it runs (F148): as its run would fill it with the typed values.
  const agentStep = current !== null && focus.plans.get(current.stepKey)?.stepKind === "agent" && current.state !== "running";
  const parts = usePromptParts(threadId, agentStep ? current.stepKey : null, runValues, overview.lastEntryNumber);
  if (current === null) {
    return null;
  }
  const step = focus.steps.get(current.stepKey);
  const summary = step?.step ?? null;
  const basePlan = focus.plans.get(current.stepKey);
  const plan = draftPlan ?? basePlan;
  const inputs = basePlan === undefined || plan === undefined ? null : stepInputs(basePlan, plan);
  const busy = runner.state.status === "submitting" || openTurn !== null;
  // Module C's decision for this viewer and step (W-22 slices 2 and 4); readiness is the plan's. Refused for who the
  // viewer is, Run says why; refused by where the Run is (closed, a turn open, another request waiting), it isn't offered.
  const running = actionOffer(overview.viewer, "run_step", current.stepKey);
  const mayRun = running !== null && running.refusal === null;
  // A saved Run's step is run by whoever takes the Run over, so Run names that way. The shared saved-Run reason can't:
  // it also refuses Cancel, whose open turn keeps the Run from changing hands (taste round 3, R3-1).
  const runUnavailable = running?.refusal == null ? undefined : running.refusal === "saved_not_host" ? t("threads.current.runSavedNotHost") : refusalText(t, running.refusal);
  // Skip refused for who the viewer is stays under Change step and says why; edit and swap stay the host's.
  const skipping = actionOffer(overview.viewer, "skip_step", current.stepKey);
  // Ready says itself by Run, and Needs input by its question; the other states name C's word, as the graph does.
  const labelled = current.state === "ready" || current.state === "needsInput" ? null : current.shown;

  const run = (stepKey: string) => {
    runner.run(stepKey, stepKey === current.stepKey ? runValues : undefined);
  };
  // Every edit opens a Thread (F51, ADR-0031 D2), never the Workflow editor.
  const editItem = (item: ItemAtVersion) => {
    preparation.prepare(editIntent(overview.projectId, item, here));
  };
  const editPinningWorkflow = () => {
    if (step !== undefined) editItem({ itemKind: "workflow", name: step.inWorkflowName ?? step.inWorkflow, reference: step.inWorkflow });
  };
  /**
   * F33's one question (ADR-0033 D4, P5's buttons): update the Workflow that pins the step,
   * in its edit Thread, or derive a new Workflow from it. Deriving edits it as this Run's
   * Workflow runs it, so its save makes the project's own copy and of each Workflow on the way
   * (ADR-0037 D7). Either way the run goes on as a branch once the new version is there (the
   * fork view's "newest versions").
   */
  const editWorkflow = (edit: WorkflowEdit) => {
    setEditing(false);
    if (step === undefined) return;
    if (edit === "update" || overview.workflow === null) {
      editPinningWorkflow();
    } else {
      const pinning = { itemKind: "workflow" as const, name: step.inWorkflowName ?? step.inWorkflow, reference: step.inWorkflow };
      preparation.prepare(editIntent(overview.projectId, pinning, here, { within: overview.workflow }));
    }
  };
  const valuesHere = mayRun && plan?.stepKind !== "human" ? runValues : null;
  const changeValues = draft.change;
  const labelOf = (name: string) => plan?.inputs.find((input) => input.name === name)?.label ?? name;

  const kind = summary === null ? null : stepKindOfDefinition(summary.kind);
  // A person step's request says what it asks, in place of the step's general description: its log entry leaves it here
  // (run-frame P1; IM-THREAD, each fact said once).
  const request = current.state === "waitingForYou" && focus.waitingRequest?.payload.kind === "human_step_requested" ? focus.waitingRequest.payload : null;
  const asks = request === null ? null : (requestTranslation(request, i18n.resolvedLanguage)?.instructions ?? request.instructions);
  const filledParts = parts?.parts.filter((part) => part.source === "selector" && part.input === null && part.text !== null).length ?? 0;
  // Stopped before this agent step (O4), it waits for Run: what it will send is shown open, to review first.
  const stopped = agentStep && overview.settings.stop_before_agent_steps === true && current.state === "ready";

  return (
    <section
      ref={section}
      aria-label={t("threads.current.label")}
      className={classNames("space-y-3 py-1 pl-3", kind === null ? "" : `app-step-strip app-step-kind-${kind}`, step?.start === "on_request" ? "app-step-optional" : "")}
    >
      <>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="app-title text-sm font-semibold">{step?.number ?? current.position}. {stepName(current.stepKey)}</h3>
            {summary === null ? null : <StepKindMark kind={summary.kind} needsPerson={basePlan?.needsPerson === true} />}
            {step?.start === "on_request" ? <OptionalChip text={t("workflows.node.onRequest")} /> : null}
            {labelled === null ? null : <Chip tone={STEP_STATE_TONE[labelled]} size="compact">{stepStateText(labelled, t)}</Chip>}
          </div>
          {asks !== null && asks !== "" ? (
            <MarkdownRenderer content={asks} className="app-markdown text-sm" />
          ) : summary === null || summary.description === "" ? null : (
            <p className="text-sm">{summary.description}</p>
          )}
          {step === undefined ? null : <StepReads reads={step.reads} node={step} bindings={focus.bindings} />}
          {current.state === "waitingForYou" && focus.waitingRequest?.payload.kind === "human_step_requested" ? (
            <WaitingStepView entry={focus.waitingRequest} payload={focus.waitingRequest.payload} />
          ) : null}
          {current.state === "failed" ? <p className="text-sm">{t(summary?.kind === "agent" ? "threads.current.failedAgent" : "threads.current.failed")}</p> : null}
          {current.state === "running" || current.state === "waitingForApproval" || current.state === "waitingForYou" || plan === undefined || inputs === null ? null : (
            // Module B plans the step's inputs and takes this run's values for its slots and tool
            // parameters (ADR-0032 D7, built provisionally, awaiting acceptance); a person step's come from earlier steps.
            <>
              <RunsWithCard plan={plan} />
              {inputs.entry === null ? null : (
                <ul className="grid gap-2">
                  <EntryQuestion input={inputs.entry} />
                </ul>
              )}
              <PlannedInputsList inputs={inputs.asked} parts={parts} stepName={stepName} values={valuesHere} pickItemsIn={overview.projectId} onChange={changeValues} onEditItem={editItem} />
              {inputs.otherReasons.length === 0 ? null : <p className={FORM_HELP_TEXT_CLASS}>{inputs.otherReasons.map((reason) => reasonText(reason, t, labelOf)).join(". ")}.</p>}
              {plan.waitsFor.length === 0 ? null : <p className={FORM_HELP_TEXT_CLASS}>{t("threads.plan.waitsFor", { steps: namesText(plan.waitsFor.map((key) => stepName(key)), t) })}</p>}
              {inputs.folded.length === 0 && plan.prompt === null && parts === null ? null : (
                <details open={stopped}>
                  <summary className="app-text-muted cursor-pointer text-xs">{t("threads.current.filled", { count: inputs.filledCount + filledParts })}</summary>
                  <div className="mt-2">
                    <PlannedInputsList prompt={plan.prompt} parts={parts} inputs={inputs.folded} stepName={stepName} values={valuesHere} pickItemsIn={overview.projectId} onChange={changeValues} onEditItem={editItem} />
                  </div>
                </details>
              )}
            </>
          )}
          {running !== null && (current.state === "ready" || current.state === "needsInput" || current.state === "failed") ? (
            <div className="flex flex-wrap items-center gap-2">
              {current.state === "failed" ? (
                <AppButton variant="primary" size="sm" disabled={busy || !mayRun} unavailable={runUnavailable} onClick={() => { run(current.stepKey); }}>{t("threads.current.tryAgain")}</AppButton>
              ) : (
                <AppButton variant="primary" size="sm" disabled={busy || !mayRun || current.state === "needsInput" || draftPending} unavailable={runUnavailable} onClick={() => { run(current.stepKey); }}>
                  {t("threads.current.run")}
                </AppButton>
              )}
              {skipping !== null || viewerIsHost ? (
              <ChangeStepMenu
                failed={current.state === "failed"}
                busy={busy}
                skip={skipping === null ? null : { unavailable: skipping.refusal === null ? undefined : refusalText(t, skipping.refusal) }}
                hostChanges={viewerIsHost}
                earlier={earlier}
                stepName={stepName}
                onSkip={() => { runner.act("skipped", current.stepKey); }}
                onEdit={() => { setEditing(true); }}
                onSwap={editPinningWorkflow}
                onNewStep={() => { preparation.prepare({ kind: "author", projectId: overview.projectId, returnTo: here, itemKind: "step", draft: null, name: "" }); }}
                onRunAgainFrom={run}
              />
              ) : null}
            </div>
          ) : null}
          {editing && step !== undefined ? (
            <div className="space-y-1">
              <p className={FORM_HELP_TEXT_CLASS}>{t("threads.current.editQuestion")}</p>
              <WorkflowEditButtons projectId={overview.projectId} workflow={step.inWorkflow} workflowName={step.inWorkflowName ?? step.inWorkflow} onChoose={editWorkflow} />
              <AppButton variant="ghost" size="xs" onClick={() => { setEditing(false); }}>{t("threads.current.cancelEdit")}</AppButton>
            </div>
          ) : null}
      </>
      {runner.state.status === "failed" ? <ErrorNotice error={runner.state.error} compact={true} /> : null}
    </section>
  );
}
